import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { trackFeature } from '../services/telemetryService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faLock,
  faArrowRight,
  faBoxesStacked,
  faBolt,
  faShieldHalved,
  faEye,
  faEyeSlash,
  faExclamationCircle,
  faHeadset,
} from '@fortawesome/free-solid-svg-icons';
import { faGoogle, faApple } from '@fortawesome/free-brands-svg-icons';
import { setCredentials } from '../store/slices/authSlice';
import { authAPI } from '../services/api';
import { useI18n } from '../hooks/useI18n';
import AuthLangSwitcher from '../components/AuthLangSwitcher';
import { openMailClient } from '../utils/mailSupport';
import '../styles/AuthModern.css';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// Declare Google Identity Services types
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          prompt: (callback?: (notification: any) => void) => void;
          renderButton: (element: HTMLElement, config: any) => void;
          revoke: (hint: string, callback?: () => void) => void;
        };
      };
    };
  }
}

const LoginModern: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useI18n();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');

  const handleGoogleLogin = async () => {
    if (!GOOGLE_CLIENT_ID) {
      setError('Google Sign-In is not configured.');
      return;
    }
    if (!window.google?.accounts?.id) {
      setError('Google Sign-In library not loaded. Please refresh the page.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response: any) => {
          try {
            const result = await authAPI.loginWithGoogle(response.credential);
            dispatch(setCredentials({ user: result.data.user, token: result.data.token }));
            trackFeature('login_google_success');
            navigate('/');
          } catch (err: any) {
            setError(err.response?.data?.error || 'Google Sign-In failed. Please try again.');
          } finally {
            setLoading(false);
          }
        },
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      window.google.accounts.id.prompt((notification: any) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          // One Tap not available — fall back to popup redirect
          const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
            `client_id=${encodeURIComponent(GOOGLE_CLIENT_ID)}` +
            `&redirect_uri=${encodeURIComponent(window.location.origin + '/auth/google/callback')}` +
            `&response_type=token%20id_token` +
            `&scope=openid%20email%20profile` +
            `&nonce=${Math.random().toString(36).substring(2)}`;
          window.location.href = authUrl;
        }
      });
    } catch (err: any) {
      setError('Google Sign-In failed. Please try again.');
      setLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    setError('🚀 Apple Sign-In coming soon! Use email & password for now.');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError(t('auth.emailRequired'));
      return;
    }

    // CAPTCHA is optional — don't block login if it failed or timed out
    // Token will be sent to backend if available; backend also treats it as optional

    setLoading(true);
    try {
      const response = await authAPI.login(email, password, captchaToken || undefined);
      dispatch(setCredentials({ user: response.data.user, token: response.data.token }));
      trackFeature('login_success');
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.error || t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-wrapper">
        {/* Left Branding Panel */}
        <div className="auth-brand-panel">
          <div className="auth-brand-logo">
            <div className="auth-brand-logo-icon">E</div>
            <div className="auth-brand-logo-text">ELVOAQ</div>
          </div>

          <h2 className="auth-brand-heading">
            {t('auth.welcomeBack').split(' ')[0]}{' '}
            <span>{t('auth.welcomeBack').split(' ').slice(1).join(' ') || t('auth.welcomeBack')}</span>
          </h2>
          <p className="auth-brand-desc">{t('auth.signInSubtitle')}</p>

          <div className="auth-features-list">
            <div className="auth-feature-item">
              <div className="auth-feature-icon">
                <FontAwesomeIcon icon={faBoxesStacked} />
              </div>
              <span className="auth-feature-text">{t('auth.benefit1')}</span>
            </div>
            <div className="auth-feature-item">
              <div className="auth-feature-icon">
                <FontAwesomeIcon icon={faBolt} />
              </div>
              <span className="auth-feature-text">{t('auth.benefit2')}</span>
            </div>
            <div className="auth-feature-item">
              <div className="auth-feature-icon">
                <FontAwesomeIcon icon={faShieldHalved} />
              </div>
              <span className="auth-feature-text">{t('auth.benefit3')}</span>
            </div>
          </div>
        </div>

        {/* Right Form Panel */}
        <div className="auth-form-panel">
          <div className="auth-card">
            <AuthLangSwitcher />

            <div className="auth-form-header">
              <h1>{t('auth.signIn')}</h1>
              <p>{t('auth.secureLogin')}</p>
            </div>

            {error && (
              <div className="auth-error">
                <FontAwesomeIcon icon={faExclamationCircle} />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="auth-form-group">
                <label className="auth-form-label">
                  <FontAwesomeIcon icon={faEnvelope} />
                  {t('auth.email')}
                </label>
                <div className="auth-input-wrapper">
                  <input
                    type="email"
                    className="auth-input"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(''); }}
                    placeholder={t('auth.emailPlaceholder')}
                    disabled={loading}
                    autoComplete="email"
                    autoFocus
                  />
                </div>
              </div>

              <div className="auth-form-group">
                <label className="auth-form-label">
                  <FontAwesomeIcon icon={faLock} />
                  {t('auth.password')}
                </label>
                <div className="auth-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="auth-input"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(''); }}
                    placeholder={t('auth.passwordPlaceholder')}
                    disabled={loading}
                    autoComplete="current-password"
                    style={{ paddingRight: '50px' }}
                  />
                  <button
                    type="button"
                    className="auth-input-icon"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
              </div>

              <div className="auth-forgot-row">
                <button
                  type="button"
                  className="auth-forgot-link"
                  onClick={() => navigate('/forgot-password')}
                >
                  {t('auth.forgotPassword')}
                </button>
              </div>

              <button type="submit" className="auth-submit-btn" disabled={loading}>
                {loading ? (
                  <>
                    <span className="auth-spinner" />
                    {t('auth.signingIn')}
                  </>
                ) : (
                  <>
                    {t('auth.signIn')}
                    <FontAwesomeIcon icon={faArrowRight} />
                  </>
                )}
              </button>
            </form>

            <div className="auth-divider">
              <span>or continue with</span>
            </div>

            <div className="auth-social-buttons">
              <button
                type="button"
                className="auth-social-btn google"
                onClick={handleGoogleLogin}
                disabled={loading}
              >
                <FontAwesomeIcon icon={faGoogle} />
                Continue with Google
              </button>
            </div>

            <div className="auth-footer-link">
              {t('auth.noAccount')}{' '}
              <button type="button" onClick={() => navigate('/signup')}>
                {t('auth.createOne')}
              </button>
            </div>

            <div className="auth-support-section">
              <p className="auth-support-text">{t('auth.havingProblems')}</p>
              <button
                type="button"
                className="auth-support-btn"
                onClick={() => openMailClient(t('auth.supportSubject'), t('auth.supportBody'))}
              >
                <FontAwesomeIcon icon={faHeadset} />
                {t('auth.reachOutToUs')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginModern;
